import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { compose, space, flexbox, layout, color } from "styled-system";
import { Menubar as PRStyledMenubar } from "primereact/menubar";
import { Menu as PRMenu } from "primereact/menu";
import Div from "@components/Div";
import { H1, H6 } from "@components/Heading";
import { Button as PRButton } from "@components/Button";
import Badge from "@components/Badge";
import Icon from "@components/Icon";
import SFBLogo from "@assets/logo.svg";
import { breakpoints } from "@utils/breakpoints";
import Sidemenu from "@components/Sidebar/Sidemenu";
import { NAV_MENU_ITEMS, NAV_BOTTOM_MENU_ITEMS, ROUTES } from "@utils/constant";
import { RenderNotifications } from "./utils";
import { getNotifications } from "@app/services/myProfile/getPersonDetails";
import { getAWSCredentials, redirectToBSSfo } from "@utils/utils";

const { tabletLandscape, mobile } = breakpoints;

const breakpoint = {
  tablet: `(min-width: ${mobile}px) and (max-width: ${tabletLandscape}px)`,
  mobile: `(max-width: ${mobile}px)`,
};

const MenuBar = styled(PRStyledMenubar)`
  position: sticky;
  z-index: 999;
  top: 0;
  left: 0;
  border: none;
  background-color: var(--white);
  border-radius: 0px;
  ${compose(space, flexbox, layout, color)};
  .p-menubar-button {
    display: none !important;
  }
  .p-menubar-end {
    & > div {
      display: flex;
      flex-direction: row;
    }
  }
  &.p-menubar.p-component {
    padding: var(--icon-fs-milli) var(--md-spacing);
    padding-right: var(--sm-spacing);
    box-shadow: var(--box-shadow);
  }
  @media ${breakpoint.mobile} {
    background-color: var(--primary);
  }
`;

export const Button = styled(PRButton)`
  width: 36px;
  height: 36px;
  background-color: var(--transparent) !important;
  border: ${({ isDetailedPage }) =>
    isDetailedPage
      ? "1px solid var(--text) !important"
      : "1px solid var(--border-line) !important"};
  justify-content: center;
  align-items: center;
  &:focus {
    box-shadow: none;
  }
  @media ${breakpoint.mobile} {
    border: 1px solid var(--text) !important;
  }
`;

const HamburgerButton = styled(Button)`
  border: 1px solid var(--text) !important;
  display: none;
  @media ${breakpoint.tablet} {
    border: 1px solid var(--text) !important;
    display: flex;
  }
  @media ${breakpoint.mobile} {
    display: none;
  }
`;

const Menu = styled(PRMenu)`
  top: 58px !important;
  width: 100%;
  border-radius: 0px;
  background-color: var(--primary) !important;
  padding: var(--xl-spacing) 0 var(--regular-spacing) 0;
  box-shadow: none !important;
  border-top: 1px solid var(--text) !important;
  &.p-menu-overlay {
    position: fixed;
  }
  span {
    color: var(--text) !important;
    line-height: var(--lh-base) !important;
    font-size: var(--fs-milli);
    &.icon::before {
      font-size: var(--fs-h5);
    }
  }
  a {
    &:focus {
      box-shadow: none !important;
    }
    &:hover {
      background-color: var(--primary-hover) !important;
    }
  }
`;

const NotificationsMenu = styled(PRMenu)`
  width: 275px !important;
  padding: 0 !important;
  top: 64px !important;
  box-shadow: var(--box-shadow);
  a {
    padding: 0 !important;
    &:focus {
      box-shadow: none !important;
    }
    &:hover {
      background-color: var(--white) !important;
    }
  }
  span {
    width: 100%;
  }
`;

const Logo = styled.img`
  ${compose(space, flexbox, layout, color)};
`;

const Header = ({ menuItems = [] }) => {
  const [notifications, setNotifications] = useState([]);
  const menu = useRef(null);
  const notificationRef = useRef(null);
  const history = useHistory();
  const [showMenuCloseIcon, setShowMenuCloseIcon] = useState(false);
  const [showSideMenu, setShowSideMenu] = useState(false);
  const handleDisplaySideMenu = () => {
    setShowSideMenu(!showSideMenu);
  };
  const [userName, setUserName] = useState("");

  const redirectToProfile = () => {
    history.push(ROUTES.MY_PROFILE.URL);
  };

  const redirectToNotification = () => {
    history.push(ROUTES.NOTIFICATIONS.URL);
  };

  const handleGoBack = () => {
    history.goBack();
  };

  useEffect(() => {
    const fetchNotifications = async () => {
      getAWSCredentials().then(async (aws) => {
        try {
          const [profile = {}] = JSON.parse(localStorage.getItem("user"));
          const { FirstName = "", LastName = "", BrickId = "" } = profile;
          setUserName(`${FirstName} ${LastName}`);
          const response = await getNotifications(aws, BrickId);
          setNotifications(response || []);
        } catch (e) {
          //catch error
        }
      });
    };
    fetchNotifications();
  }, []);

  const { headerTitle = "", isDetailPage = "" } = useSelector(
    (state) => state.headerReducer
  );

  const unreadMessages = notifications?.filter((val = {}) => {
    const { IsRead: isRead = [] } = val || {};
    return isRead.includes(false);
  });

  const start = (
    <Div display="flex">
      <HamburgerButton mr="var(--md-spacing)" onClick={handleDisplaySideMenu}>
        <Icon
          className="icon ph-text-align-left-bold"
          color="var(--text)"
          fontSize="var(--icon-fs-milli)"
        />
      </HamburgerButton>
      <Div display={["none", "none", "flex", "flex"]}>
        {isDetailPage && (
          <Button mr="var(--md-spacing)" onClick={handleGoBack} isDetailedPage>
            <Icon
              className="icon ph-arrow-left-bold"
              color="var(--text)"
              fontSize="var(--fs-h5)"
            />
          </Button>
        )}
      </Div>
      <H1 display={["none", "flex", "flex", "flex"]}>{headerTitle}</H1>
      <Logo
        src={SFBLogo}
        alt="icon"
        display={["flex", "none", "none", "none"]}
      />
    </Div>
  );

  const handleMobileMenu = (e) => {
    menu.current.toggle(e);
  };

  const end = (
    <Div alignItems="center">
      <H6 display={["none", "none", "flex", "flex"]}>{userName}</H6>
      {showMenuCloseIcon ? (
        <Button className="p-overlay-badge" ml="var(--sm-spacing)">
          <Icon
            className="icon ph-x-bold"
            color="var(--text)"
            fontSize="var(--fs-h5)"
          />
        </Button>
      ) : (
        <>
          <Div
            ml="var(--sm-spacing)"
            display={["none", "none", "block", "block"]}
          >
            {unreadMessages.length > 0 ? (
              <Button
                className="p-overlay-badge"
                onClick={(e) => notificationRef.current.toggle(e)}
              >
                <Icon
                  className="icon ph-envelope-open-bold"
                  fontSize="var(--fs-h5)"
                  color={["var(--text)", "var(--alert)"]}
                />
                <Badge value={unreadMessages.length} />
              </Button>
            ) : (
              <Button
                className="p-overlay-badge"
                onClick={redirectToNotification}
              >
                <Icon
                  className="icon ph-envelope-simple-bold"
                  fontSize="var(--fs-h5)"
                  color={["var(--text)", "var(--blue-light)"]}
                />
              </Button>
            )}
          </Div>
          <Div display={["none", "none", "block", "block"]}>
            <Button ml="var(--sm-spacing)" onClick={redirectToProfile}>
              <Icon
                className="icon ph-user-bold"
                fontSize="var(--fs-h5)"
                color={["var(--text)", "var(--blue-light)"]}
              />
            </Button>
          </Div>
          <Div
            ml="var(--sm-spacing)"
            display={["block", "block", "none", "none"]}
          >
            <Button
              className="p-overlay-badge"
              onClick={redirectToNotification}
            >
              {unreadMessages.length > 0 ? (
                <>
                  <Icon
                    className="icon ph-envelope-open-bold"
                    fontSize="var(--fs-h5)"
                    color={["var(--text)", "var(--alert)"]}
                  />

                  <Badge value={unreadMessages.length} />
                </>
              ) : (
                <Icon
                  className="icon ph-envelope-simple-bold"
                  fontSize="var(--fs-h5)"
                  color={["var(--text)", "var(--blue-light)"]}
                />
              )}
            </Button>
          </Div>
          <Div display={["block", "block", "none", "none"]}>
            <Button ml="var(--sm-spacing)" onClick={handleMobileMenu}>
              <Icon
                className="icon ph-user-bold"
                fontSize="var(--fs-h5)"
                color={["var(--text)", "var(--blue-light)"]}
              />
            </Button>
          </Div>
        </>
      )}
    </Div>
  );

  const handleMenu = () => {
    setShowMenuCloseIcon(!showMenuCloseIcon);
  };

  const config = {
    navItems: {
      navMenuItems: NAV_MENU_ITEMS,
      navBottomMenuItems: NAV_BOTTOM_MENU_ITEMS,
    },
    onClose: handleDisplaySideMenu,
    visibility: showSideMenu,
  };

  const notificationItems = RenderNotifications(unreadMessages);

  const handleRedirection = (obj) => {
    const { redirectTo, isExternalPage } = obj || {};
    if (!redirectTo) return;
    if (isExternalPage) {
      window.open(redirectTo, "_blank");
    } else if (redirectTo === ROUTES.LOGIN.URL) {
      localStorage.clear();
      redirectToBSSfo();
    } else {
      history.push(redirectTo);
    }
  };

  const items = menuItems.map((o) => ({
    ...o,
    command: () => handleRedirection(o),
  }));

  items[0].label = userName || "";

  return (
    <>
      <Sidemenu config={config} display={["none", "flex", "flex", "none"]} />
      <MenuBar start={start} end={end} width={1} display="flex" />
      <Menu
        model={items}
        popup
        ref={menu}
        onShow={handleMenu}
        onHide={handleMenu}
      />
      <NotificationsMenu
        model={notificationItems}
        popup
        ref={notificationRef}
      />
    </>
  );
};

Header.propTypes = {
  setPageTitle: PropTypes.string,
  menuItems: PropTypes.array,
};

export default Header;
